import { useState } from 'react';
import { useContainerWidth } from '../../hooks/useContainerWidth';

export interface SparkPoint {
  key: string;
  label: string;
  value: number;
}

interface Props {
  points: SparkPoint[];
  color: string;
  formatValue: (n: number) => string;
  height?: number;
  ariaLabel: string;
}

const PAD = { top: 10, right: 10, bottom: 22, left: 10 };

/** 2px line with a hover crosshair + tooltip. Single series, so no legend — the heading names it. */
export function Sparkline({ points, color, formatValue, height = 120, ariaLabel }: Props) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const plotW = width - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;
  const max = Math.max(1, ...points.map(p => p.value));
  const x = (i: number) => PAD.left + (points.length > 1 ? (i / (points.length - 1)) * plotW : plotW / 2);
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH;
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    if (points.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left - PAD.left;
    const i = Math.round((px / Math.max(1, plotW)) * (points.length - 1));
    setHover(Math.min(points.length - 1, Math.max(0, i)));
  }

  return (
    <div ref={ref} className="relative w-full">
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={ariaLabel}
        className="block select-none"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <line x1={PAD.left} x2={width - PAD.right} y1={y(0)} y2={y(0)} stroke="#e2e8f0" strokeWidth={1} />
        {points.length > 1 && <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
        {points.length === 1 && <circle cx={x(0)} cy={y(points[0].value)} r={4} fill={color} />}
        {hover !== null && points[hover] && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotH} stroke="#94a3b8" strokeDasharray="3 3" />
            <circle cx={x(hover)} cy={y(points[hover].value)} r={5} fill={color} stroke="#fff" strokeWidth={2} />
          </g>
        )}
        {points.length > 0 && (
          <>
            <text x={PAD.left} y={height - 6} fontSize={11} fill="#475569">{points[0].label}</text>
            {points.length > 1 && (
              <text x={width - PAD.right} y={height - 6} fontSize={11} fill="#475569" textAnchor="end">
                {points[points.length - 1].label}
              </text>
            )}
          </>
        )}
      </svg>
      {hover !== null && points[hover] && (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs shadow"
          style={{ left: Math.min(width - 120, Math.max(0, x(hover) - 50)), top: 0 }}
        >
          <span className="text-slate-600">{points[hover].label}</span>{' '}
          <span className="font-medium text-slate-800 tabular-nums">{formatValue(points[hover].value)}</span>
        </div>
      )}
    </div>
  );
}
